import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { logAudit } from '../lib/auditLog';

function ResetPassword() {
  const navigate = useNavigate();
  const [recoveryReady, setRecoveryReady] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        setRecoveryReady(true);
      }
    });

    // Fallback: session may already be established before listener mounted
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setRecoveryReady(true);
    });

    // If neither path fires within 3s, the link is invalid/expired
    const timeout = setTimeout(() => {
      setInvalid(true);
    }, 3000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  // Once recoveryReady fires, clear the invalid timeout effect
  useEffect(() => {
    if (recoveryReady) setInvalid(false);
  }, [recoveryReady]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    const { error } = await supabase.auth.updateUser({ password: newPassword });

    if (error) {
      setError('Failed to reset password. Please try again.');
      setLoading(false);
    } else {
      logAudit('password_reset', 'auth', null, {});
      setSuccess(true);
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-black">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
          <div className="bg-green-50 text-green-700 p-4 rounded-md mb-4">
            Password reset successfully!
          </div>
          <button
            onClick={() => navigate('/')}
            className="bg-brand-coral text-white py-2 px-6 rounded-md hover:bg-brand-coral-dark"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (invalid && !recoveryReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-black">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
          <div className="bg-red-50 text-red-600 p-4 rounded-md mb-4">
            This reset link is invalid or has expired. Please request a new one.
          </div>
          <button
            onClick={() => navigate('/login')}
            className="bg-brand-coral text-white py-2 px-6 rounded-md hover:bg-brand-coral-dark"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  if (!recoveryReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-black">
        <div className="text-white text-sm">Verifying reset link...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-black">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h1 className="text-2xl font-bold text-center text-brand-black mb-6">
          Reset Password
        </h1>

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleResetPassword} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-coral"
              placeholder="At least 8 characters"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-coral"
              placeholder="Confirm new password"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-coral text-white py-2 px-4 rounded-md hover:bg-brand-coral-dark disabled:opacity-50"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ResetPassword;
